
  import ScreenSize from '@/mixins/ScreenSize';
  import {mapSectionComponents} from '@/helper/SectionHelper';
  import {getMetaTags, replaceOrganizationDataInMetaData} from '@/helper/Seo';
  import LazyHydrate from 'vue-lazy-hydration';
  import {scopeHelper} from '@/helper/ScopeHelper';
  import {prepareApiUrl} from '@/helper/UrlHelper';
  import startCase from 'lodash/startCase';

  export default {
    name: 'Organization',
    components: {
      LazyHydrate,
    },
    mixins: [ScreenSize],
    async asyncData({app, error, redirect, route, store, $cachedStrapi}) {
      const page = await $cachedStrapi
        .findOne('pages', 'organizationdetails')
        .catch((e) => {
          if (e.statusCode !== 404) {
            throw e;
          }
        });

      if (!scopeHelper(page, process.env.SCOPE_CANDIDATE)) {
        error({statusCode: 404, message: 'Page not found'});
        return;
      }

      const organization = await store.dispatch(
        'api/organization/getOrganizationBySlug',
        route.params.slug
      );

      // check if organization is found and active
      if (
        organization &&
        (!organization.isActive || !organization.publicProfile)
      ) {
        const slug = route.params.slug;
        const readableSlug = startCase(slug.replace(/-/g, ' '));

        // Query parameters for inactive jobs for appended
        const currentQuery = route.query;
        const newQueryParam = {
          ...currentQuery,
          mtm_group: 'organization-deactivated',
          search: readableSlug,
        };

        const redirectRoute = app.localePath({
          name: 'organizations',
          query: newQueryParam,
        });

        // Redirect the user to the new URL (with status code 301)
        redirect(301, redirectRoute);
        return;
      } else if (!organization) {
        const slug = route.params.slug;
        const readableSlug = startCase(slug.replace(/-/g, ' '));

        // Query parameters for inactive jobs for appended
        const currentQuery = route.query;
        const newQueryParam = {
          ...currentQuery,
          mtm_group: 'organization-not-found',
          search: readableSlug,
        };

        const redirectRoute = app.localePath({
          name: 'organizations',
          query: newQueryParam,
        });

        // Redirect the user to the new URL (with status code 301)
        redirect(302, redirectRoute);
        return;
      }

      const sections = await mapSectionComponents(
        page.sections,
        async function (section) {
          switch (section.component) {
            case 'SectionOrganizationDetails':
              section.organization = organization;
              break;

            case 'SectionSmallHero':
              if (section.title) {
                section.title = section.title.replace(
                  /%organization_name/g,
                  organization.name
                );
              }
              section.organization_id = organization.id;
              break;

            case 'SectionBreadcrumbs':
              section.breadcrumbs = [
                {
                  text: app.i18n.t('page.index'),
                  disabled: false,
                  to: app.localePath({
                    name: 'index',
                  }),
                  nuxt: true,
                  exact: true,
                },
                {
                  text: app.i18n.t('page.organizations'),
                  disabled: false,
                  to: app.localePath(
                    store.getters['breadcrumbs/hasOrganizationListingRoute']
                      ? store.state.breadcrumbs.lastOrganizationListingRoute
                      : {
                          name: 'organizations',
                        }
                  ),
                  nuxt: true,
                  exact: true,
                },
                {
                  text: organization.name,
                  disabled: true,
                  to: app.localePath({
                    name: 'organizations-slug',
                    params: {
                      slug: organization.slug,
                    },
                  }),
                  nuxt: true,
                  exact: true,
                },
              ];
              break;

            case 'SectionSeoContent':
              if (
                section.seo_content_config &&
                section.seo_content_config.seo_contents &&
                section.seo_content_config.seo_contents[0]
              ) {
                section.seoContent = await $cachedStrapi.findOne(
                  'seo-contents',
                  section.seo_content_config.seo_contents[0].id
                );
              }
              break;
          }
          if (organization.logo) {
            section.logo = {
              url: prepareApiUrl(process.env.NUXT_ENV_LEGACY_AGENCY_LOGO_URL, {
                routeParameters: {
                  id: organization.id,
                },
                getParameters: {},
              }),
              alt: app.i18n.t('organization.logo_alt', {
                organization_name: organization.name,
              }),
              title: app.i18n.t('organization.logo_title', {
                organization_name: organization.name,
              }),
            };
          }
        }
      );

      // Meta-Data mit Werten von der Firma
      const metaData = replaceOrganizationDataInMetaData(
        page.meta ?? {},
        organization
      );
      //metaData.title = organization.name;
      //metaData.description = organization.shortDescription;

      return {
        metaData,
        sections,
      };
    },
    data() {
      return {
        metaData: {},
        sections: [],
      };
    },
    head() {
      return {
        title: this.metaData.title,
        meta: getMetaTags(this.metaData),
      };
    },
  };
